import { ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { captureException } from "@sentry/browser";
import { useTranslation } from "react-i18next";
import { useModal } from "react-modal-hook";
import { useSwipeable } from "react-swipeable";
import { MIME_TYPE, OBJECT_TYPE } from "common/constants";
import { buildLink } from "common/routing";
import { isImage, isVideo } from "common/utils/files";
import { useStreetViewImage } from "common-client/utils/useStreetViewImage";
import { useGetCarouselDocumentsQuery } from "../../../generated/graphql";
import { AuthContext } from "../../Authorization/AuthContext";
import { Button } from "../Buttons/Button";
import Modal from "../Modal";
import { Body } from "../Typography";
import {
  ActionButtonRow,
  CarouselContainer,
  CarouselItem,
  IndexIndicator,
  IndexIndicators,
  InnerContainer,
  Next,
  Previous,
} from "./__styles__/Carousel";
import FullPageCarouselModal from "./FullPageCarouselModal";
import { ViewOnStreetViewButton } from "./StreetViewImage";

export type CarouselDocument = {
  mimeType: MIME_TYPE;
  url: string;
  altText?: Maybe<string>;
  actionButton?: ReactNode;
  id: Maybe<string>;
};

type CarouselSize = "small" | "large" | "fullscreen";

export const Carousel = ({
  documents,
  size = "large",
  toggleFullscreenModal,
  index,
  setIndex,
}: {
  documents: Array<CarouselDocument>;
  size?: CarouselSize;
  toggleFullscreenModal: () => void;
  index: number;
  setIndex: (newIndex: number) => void;
}) => {
  const fullscreen = size === "fullscreen";

  const numDocuments = documents.length;

  const updateIndex = useCallback(
    (newIndex: number) => {
      if (newIndex < 0) {
        newIndex = numDocuments - 1;
      } else if (newIndex >= documents.length) {
        newIndex = 0;
      }
      setIndex(newIndex);
    },
    [numDocuments, documents.length, setIndex],
  );

  const handlers = useSwipeable({
    onSwipedLeft: () => updateIndex(index + 1),
    onSwipedRight: () => updateIndex(index - 1),
  });

  const getRendererForType = ({
    mimeType,
    index,
  }: {
    mimeType: MIME_TYPE;
    index: number;
  }) => {
    if (isImage(mimeType)) {
      return (document: CarouselDocument) => (
        <img
          data-testid={`property-image-${index}`}
          src={document.url}
          alt={document.altText ?? "A photo of the property"}
        />
      );
    } else {
      return () => null;
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!fullscreen) return;

      if (e.key === "ArrowLeft") {
        updateIndex(index - 1);
      } else if (e.key === "ArrowRight") {
        updateIndex(index + 1);
      } else if (e.key === "Escape") {
        toggleFullscreenModal();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [index, fullscreen, toggleFullscreenModal, updateIndex]);

  return (
    <CarouselContainer
      {...handlers}
      size={size}
      data-testid="carousel-container"
    >
      <InnerContainer
        data-testid="inner-container"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {documents.map((document, i) => (
          <CarouselItem key={`document-${i}`}>
            {!fullscreen && (
              <ActionButtonRow>
                {document.actionButton}
                {i === index && (
                  <Button
                    icon="expand"
                    size="small"
                    variant="secondary"
                    onClick={toggleFullscreenModal}
                    data-testid="fullscreen-toggle"
                    aria-label="fullscreen-toggle"
                  />
                )}
              </ActionButtonRow>
            )}

            {getRendererForType({ mimeType: document.mimeType, index: i })(
              document,
            )}
          </CarouselItem>
        ))}
      </InnerContainer>
      {numDocuments > 1 && (
        <>
          <Previous
            icon="chevron-left"
            size="small"
            variant={fullscreen ? "outlineInverted" : "secondary"}
            aria-label="previous"
            data-testid="previous-button"
            onPress={() => updateIndex(index - 1)}
          />
          <Next
            icon="chevron-right"
            size="small"
            variant={fullscreen ? "outlineInverted" : "secondary"}
            aria-label="next"
            data-testid="next-button"
            onPress={() => updateIndex(index + 1)}
          />

          <IndexIndicators data-testid="index-indicators">
            {documents.map((_, i) => {
              return (
                <IndexIndicator
                  aria-label={`indicator-${i}`}
                  active={i === index}
                  onPress={() => updateIndex(i)}
                  key={`indicator-${i}`}
                />
              );
            })}
          </IndexIndicators>
        </>
      )}
    </CarouselContainer>
  );
};

const CarouselWrapper = ({
  documents,
  size,
  objectId,
  objectType,
  objectPartitionKey,
}: {
  documents: Array<CarouselDocument>;
  size: CarouselSize;
  objectId: string;
  objectType: OBJECT_TYPE;
  objectPartitionKey?: Maybe<string>;
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const authContext = useContext(AuthContext);

  const [showFullscreenCarousel, hideFullscreenCarousel] = useModal(() => {
    return (
      <Modal overlayClassName={"modal-overlay dark"}>
        <FullPageCarouselModal
          activeIndex={activeIndex}
          setActiveIndex={setActiveIndex}
          documents={documents}
          modalClose={hideFullscreenCarousel}
          objectId={objectId}
          objectType={objectType}
          objectPartitionKey={objectPartitionKey}
          authContext={authContext}
        />
      </Modal>
    );
  }, [activeIndex, documents]);

  return (
    <Carousel
      documents={documents}
      size={size}
      index={activeIndex}
      setIndex={setActiveIndex}
      toggleFullscreenModal={showFullscreenCarousel}
    />
  );
};

export const ObjectCarousel = ({
  objectId,
  objectType,
  objectPartitionKey,
  coverImageDocumentUploadId,
  size = "large",
  label,
  streetViewInfo,
}: {
  objectId: string;
  objectType: OBJECT_TYPE;
  objectPartitionKey?: Maybe<string>;
  coverImageDocumentUploadId: Maybe<string>;
  size?: CarouselSize;
  label?: Maybe<string>;
  streetViewInfo?: {
    latitude: number;
    longitude: number;
  };
}) => {
  const { t } = useTranslation();
  const { imageURL, loading: loadingStreetView } = useStreetViewImage({
    latitude: streetViewInfo?.latitude,
    longitude: streetViewInfo?.longitude,
    size: "640x640", // this is the largest image we can get from streetview. We will fetch it once so it works in the expanded view.
    onError: captureException,
  });

  const { data: carouselData, loading: carouselLoading } =
    useGetCarouselDocumentsQuery({
      variables: {
        objectId,
        objectType,
        objectPartitionKey: objectPartitionKey ?? undefined,
      },
      skip: !objectId,
    });

  let documents: Array<CarouselDocument> = [];

  if (imageURL && streetViewInfo) {
    const { longitude, latitude } = streetViewInfo;
    const streetViewImage = {
      mimeType: MIME_TYPE.JPEG,
      url: imageURL,
      actionButton: (
        <ViewOnStreetViewButton latitude={latitude} longitude={longitude} />
      ),
      altText: label && t("alt-text-google-streetview", { address: label }),
      id: null,
    };

    documents.push(streetViewImage);
  }

  if (!carouselLoading && carouselData?.getDocumentUploadsForTargetObject) {
    const documentUploads = carouselData.getDocumentUploadsForTargetObject;

    for (const documentUpload of documentUploads) {
      if (!isVideo(documentUpload.mimeType)) {
        const document = {
          mimeType: documentUpload.mimeType,
          url: buildLink("documentUploadFile", {
            id: documentUpload.id,
          }),
          altText:
            label && t("alt-text-google-property-image", { address: label }),
          id: documentUpload.id,
        };

        documentUpload.id === coverImageDocumentUploadId
          ? documents.unshift(document)
          : documents.push(document);
      }
    }
  }

  if (carouselLoading || loadingStreetView) {
    return (
      <Body size="default" type="emphasis" color="contentSecondary">
        {t("common-loading")}
      </Body>
    );
  }

  if (
    documents.length === 0 ||
    !carouselData?.getDocumentUploadsForTargetObject
  )
    return null;

  return (
    <CarouselWrapper
      documents={documents}
      size={size}
      objectId={objectId}
      objectType={objectType}
      objectPartitionKey={objectPartitionKey}
    />
  );
};
